import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ArrowRight, ShoppingCart } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { formatPrice } from '@/lib/api';
import { Badge } from '@/components/ui/badge';

function MiniProductCard({ product }) {
    const { addToCart } = useCart();
    const hasDiscount = product.sale_price && product.sale_price < product.price;
    const discount = hasDiscount ? Math.round((1 - product.sale_price / product.price) * 100) : 0;

    return (
        <div className="flex-shrink-0 w-[160px] sm:w-[190px] lg:w-[210px] flex flex-col group" data-testid={`carousel-product-${product.id}`}>
            <Link to={`/product/${product.slug}`} className="block relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-pink-50/30 to-gray-50 mb-2">
                <img src={product.images?.[0] || '/placeholder.png'} alt={`${product.name} - ${product.category_name || 'BeautyByChancy'} skincare product`}
                    width={300} height={300}
                    sizes="(max-width: 640px) 45vw, 220px"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                {hasDiscount && <Badge className="absolute top-1.5 left-1.5 bg-pink-600 text-white text-[9px] px-1.5 py-0.5 rounded-full font-body shadow-md">-{discount}%</Badge>}
                {product.stock <= 0 && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><span className="text-white text-[10px] font-body bg-black/60 px-2 py-0.5 rounded-full">Sold Out</span></div>}
            </Link>
            <Link to={`/product/${product.slug}`}>
                <h4 className="text-xs sm:text-sm font-body font-medium text-gray-900 line-clamp-2 leading-tight hover:text-pink-600 transition-colors h-[2.4rem] sm:h-[2.6rem]">{product.name}</h4>
            </Link>
            <div className="flex items-center gap-1.5 mt-auto pt-1">
                <span className="text-sm font-bold text-pink-600 font-body">{formatPrice(product.sale_price || product.price)}</span>
                {hasDiscount && <span className="text-[10px] text-gray-400 line-through font-body">{formatPrice(product.price)}</span>}
            </div>
            <button onClick={() => { if (product.stock > 0) addToCart(product.id); }} disabled={product.stock <= 0}
                className="mt-1.5 w-full flex items-center justify-center gap-1.5 bg-pink-600 text-white py-1.5 rounded-full text-[11px] sm:text-xs font-medium font-body hover:bg-pink-700 hover:shadow-md hover:shadow-pink-600/20 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.97]"
                data-testid={`carousel-add-cart-${product.id}`}>
                <ShoppingCart className="w-3 h-3" /> Add to Cart
            </button>
        </div>
    );
}

export default function CategoryProductCarousel({ category, products }) {
    const scrollRef = useRef(null);
    const scroll = (dir) => { if (scrollRef.current) scrollRef.current.scrollBy({ left: dir === 'left' ? -400 : 400, behavior: 'smooth' }); };
    if (!products || products.length === 0) return null;

    return (
        <section className="py-6 sm:py-8 cv-auto" data-testid={`category-carousel-${category.slug}`}>
            <div className="max-w-7xl mx-auto px-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="w-1 h-7 bg-pink-600 rounded-full" />
                        <h2 className="font-heading text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">{category.name}</h2>
                    </div>
                    <Link to={`/shop/${category.slug}`} className="flex items-center gap-1 text-sm font-body font-medium text-pink-600 hover:text-pink-700 transition-colors bg-pink-50 hover:bg-pink-100 px-3 py-1.5 rounded-full" data-testid={`view-all-${category.slug}`}>
                        View All <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
                <div className="relative group/carousel">
                    <button onClick={() => scroll('left')} className="absolute left-0 top-[40%] -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/95 shadow-lg border border-gray-100 flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-pink-50 -ml-2" data-testid={`carousel-prev-${category.slug}`}>
                        <ChevronLeft className="w-4 h-4 text-gray-700" />
                    </button>
                    <div ref={scrollRef} className="flex gap-3 sm:gap-4 overflow-x-auto hide-scrollbar scroll-smooth pb-2">
                        {products.map(p => <MiniProductCard key={p.id} product={p} />)}
                    </div>
                    <button onClick={() => scroll('right')} className="absolute right-0 top-[40%] -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/95 shadow-lg border border-gray-100 flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-pink-50 -mr-2" data-testid={`carousel-next-${category.slug}`}>
                        <ChevronRight className="w-4 h-4 text-gray-700" />
                    </button>
                </div>
            </div>
        </section>
    );
}
